import React, { useMemo, useState } from 'react';
import { WORKSHEET_QUESTION_TYPES } from './utils/worksheetQuestionTypes';
// src/WorksheetModule.js
// Plain-JS modal helpers + lightweight in-app renderer for Worksheet blocks.
// Exports:
//   - renderWorksheetModalContent(containerEl, data?)
//   - handleWorksheetModalSave(containerEl)
//   - WorksheetComponent (React)  -> shows a simple preview in-page
//
// Notes:
// - No TypeScript, no ReactDOM usage. We only set containerEl.innerHTML.
// - This avoids "container.appendChild is not a function" errors in React modals.

/** Render simple modal UI for creating/editing a worksheet.
 *  @param {HTMLElement} containerEl - Host element your modal provides.
 *  @param {object} data - Optional existing worksheet data.
 */
export function renderWorksheetModalContent(containerEl, data = {}) {
  if (!containerEl) return;

  const doc = containerEl.ownerDocument || document;
  const esc = (v) => (v == null ? '' : String(v).replace(/[&<>"]/g, (m) => ({
    '&': '&amp;','<': '&lt;','>': '&gt;','"': '&quot;'
  }[m])));

  const html = `
    <div class="space-y-4">
      <div class="flex items-center gap-2">
        <input id="ws-usepoints" type="checkbox" ${data.usePoints === false ? '' : 'checked'} />
        <label for="ws-usepoints" class="text-sm font-medium text-gray-700">Use points</label>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label for="ws-title" class="block text-sm font-medium text-gray-700">Worksheet title</label>
          <input id="ws-title" type="text"
                 class="mt-1 block w-full rounded border border-gray-300 p-2"
                 placeholder="Worksheet"
                 value="${esc(data.title) || 'Worksheet'}" />
        </div>
        <div>
          <label for="ws-points" class="block text-sm font-medium text-gray-700">Total points (optional)</label>
          <input id="ws-points" type="number" min="0"
                 class="mt-1 block w-full rounded border border-gray-300 p-2"
                 placeholder="e.g., 20"
                 value="${esc(data.totalPoints || '')}" ${data.usePoints === false ? 'disabled' : ''} />
        </div>
      </div>

      <div>
        <label for="ws-desc" class="block text-sm font-medium text-gray-700">Description / Instructions</label>
        <textarea id="ws-desc" rows="3"
                  class="mt-1 block w-full rounded border border-gray-300 p-2"
                  placeholder="What should students do?">${esc(data.description || '')}</textarea>
      </div>

      <div class="p-3 rounded bg-gray-50 border border-gray-200 text-sm text-gray-700">
        <strong>Heads-up:</strong> This modal is a lightweight shell so you can add a
        Worksheet block. Question details can be edited later in the block UI.
      </div>
    </div>
  `;

  // Only write innerHTML; some modal hosts pass non-DOM container objects.
  if (typeof containerEl.innerHTML === 'string') {
    containerEl.innerHTML = html;
  }

  // Provide a getter so Save can read the draft back out.
  containerEl._getWorksheetDraft = function () {
    const titleEl = doc.getElementById('ws-title');
    const descEl  = doc.getElementById('ws-desc');
    const ptsEl   = doc.getElementById('ws-points');
    const usePtsEl = doc.getElementById('ws-usepoints');
    return {
      type: 'worksheet',
      title: titleEl ? titleEl.value.trim() || 'Worksheet' : 'Worksheet',
      description: descEl ? descEl.value.trim() : '',
      totalPoints: usePtsEl && usePtsEl.checked && ptsEl && ptsEl.value !== '' ? Number(ptsEl.value) : undefined,
      usePoints: usePtsEl ? usePtsEl.checked : true,
      questions: Array.isArray(data.questions) ? data.questions : []
    };
  };
}

/** Called by the modal's Save button. Returns the worksheet block payload. */
export function handleWorksheetModalSave(containerEl) {
  if (containerEl && typeof containerEl._getWorksheetDraft === 'function') {
    return containerEl._getWorksheetDraft();
  }
  return null;
}

/** Simple in-app renderer (React) so the worksheet block shows in the page. */
export function normalizeWorksheetQuestions(questions) {
  return (questions || []).map((q, idx) => ({
    id: q?.id || `q${idx + 1}`,
    type: q?.type || 'short-answer',
    label: q?.label || `${idx + 1}.`,
    prompt: q?.prompt || q?.title || q?.question || q?.content || 'Question',
    placeholder: q?.placeholder || 'Your answer',
    points: typeof q?.points === 'number' ? q.points : 1,
    options: Array.isArray(q?.options) ? q.options : [],
    correctAnswer: q?.correctAnswer ?? q?.answer ?? '',
  }));
}

export function WorksheetComponent({ worksheet, isEditMode }) {
  const data = worksheet || { title: 'Worksheet', description: '', questions: [] };
  const normalizedQuestions = useMemo(
    () => normalizeWorksheetQuestions(Array.isArray(data.questions) ? data.questions : []),
    [data.questions]
  );
  const showPoints = data.usePoints !== false;
  const total = showPoints
    ? normalizedQuestions.reduce((sum, q) => sum + (q.points || 0), 0)
    : 0;

  const containerId = `worksheet-${data.id || 'temp'}`;
  const [responses, setResponses] = useState({});

  const handleChange = (qid, value) => {
    setResponses((prev) => ({ ...prev, [qid]: value }));
  };

  const exportAnswers = () => {
    const payload = {
      worksheetId: data.id || containerId,
      title: data.title || 'Worksheet',
      exportedAt: new Date().toISOString(),
      responses: normalizedQuestions.map((q) => ({
        id: q.id,
        label: q.label,
        prompt: q.prompt,
        answer: responses[q.id] ?? '',
      })),
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(data.title || 'worksheet').replace(/\s+/g, '_').toLowerCase()}_answers.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const importInputId = `${containerId}-answer-import`;

  const handleAnswerImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const raw = await file.text();
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.responses)) {
        alert('Invalid answers file.');
        return;
      }
      const next = {};
      parsed.responses.forEach((item) => {
        if (item && item.id) {
          next[item.id] = item.answer ?? '';
        }
      });
      setResponses((prev) => ({ ...prev, ...next }));
      alert('Answers imported.');
    } catch (err) {
      console.error(err);
      alert('Unable to import answers. Please check the file.');
    } finally {
      event.target.value = '';
    }
  };

  const printWorksheetOnly = (id) => {
    try {
      const source = document.getElementById(id);
      if (!source) {
        alert('Worksheet not found for printing.');
        return;
      }

      const formData = {};
      const srcEls = Array.from(source.querySelectorAll('input, textarea, select'));
      srcEls.forEach((el, i) => {
        const key = `el_${i}`;
        if (el.type === 'checkbox' || el.type === 'radio') {
          formData[key] = el.checked;
          formData[`${key}_value`] = el.value;
        } else {
          formData[key] = el.value;
        }
        formData[`${key}_type`] = el.type;
      });

      const clone = source.cloneNode(true);
      const cloneEls = Array.from(clone.querySelectorAll('input, textarea, select'));
      cloneEls.forEach((el, i) => {
        const key = `el_${i}`;
        const type = formData[`${key}_type`];
        const val = formData[key];
        if (type === 'checkbox' || type === 'radio') {
          const savedVal = formData[`${key}_value`];
          if (type === 'radio') {
            const cb = document.createElement('input');
            cb.type = 'checkbox';
            cb.name = el.name;
            cb.className = el.className;
            cb.id = el.id;
            cb.style.cssText = el.style.cssText;
            el.parentNode.replaceChild(cb, el);
            el = cb;
          }
          if (val) {
            el.setAttribute('checked', 'checked');
            el.checked = true;
          } else {
            el.removeAttribute('checked');
            el.checked = false;
          }
          if (savedVal !== undefined) {
            el.setAttribute('value', savedVal);
            el.value = savedVal;
          }
        } else {
          const txt = document.createElement('div');
          txt.className = 'print-response';
          txt.textContent = val || '';
          el.replaceWith(txt);
        }
      });

      Array.from(clone.querySelectorAll('.no-print, .worksheet-print-button, button')).forEach((el) =>
        el.remove()
      );

      const titleEl = clone.querySelector('.worksheet-title,h1,h2,h3');
      const title = titleEl ? titleEl.textContent.trim() : 'Worksheet';
      if (titleEl) {
        titleEl.remove();
      }
      const headerHtml = `<div class="worksheet-header"><h2>${title}</h2></div>`;
      const styles =
        '@page{size:8.5in 11in;margin:0.75in;}' +
        '*{box-sizing:border-box;}' +
        'body{font-family:Arial,Helvetica,sans-serif;font-size:11pt;line-height:1.7;color:#1a1a1a;background:white;padding:0;margin:0;}' +
        '.worksheet-block{max-width:100%;margin:0;padding:0;border:none !important;background:white !important;}' +
        '.worksheet-header{margin-bottom:1.5em;padding-bottom:0.75em;border-bottom:3px solid #000;}' +
        '.worksheet-header h2{text-align:center;font-size:20pt;font-weight:bold;margin:0;padding:0;letter-spacing:0.5pt;text-transform:uppercase;}' +
        '.print-header{margin:0 0 2em 0;padding:1em;border:2px solid #333;background:#f9f9f9;display:grid;grid-template-columns:repeat(3,1fr);gap:1em;}' +
        '.print-header > div{display:flex;flex-direction:column;}' +
        '.print-header label{font-weight:bold;font-size:9pt;color:#333;margin-bottom:0.35em;text-transform:uppercase;letter-spacing:0.3pt;}' +
        '.print-header input{border:none !important;border-bottom:1.5pt solid #333 !important;background:transparent !important;padding:0.25em 0 !important;font-size:10pt !important;font-family:Arial,sans-serif !important;}' +
        '.worksheet-question{margin:0 0 2.5em 0;padding:0;page-break-inside:avoid;display:flex;gap:0.75em;align-items:flex-start;}' +
        '.worksheet-question:last-of-type{margin-bottom:1em;}' +
        '.worksheet-question > span:first-child{font-weight:bold;font-size:12pt;min-width:2em;display:inline-block;}' +
        '.worksheet-question p{font-weight:bold;font-size:11pt;margin:0 0 0.75em 0;line-height:1.5;color:#000;}' +
        '.print-response{padding:0.75em;border:1.5pt solid #666;min-height:2.5em;background:white;white-space:pre-wrap;word-wrap:break-word;font-size:10pt;line-height:1.6;margin-top:0.5em;}' +
        '.print-response:empty::before{content:"";display:block;min-height:2em;}' +
        'input[type="text"],input[type="date"],textarea{border:1.5pt solid #666 !important;padding:0.75em !important;min-height:2.5em;background:white !important;font-family:Arial,sans-serif !important;font-size:10pt !important;margin-top:0.5em;}' +
        'input[type="checkbox"]{display:inline-flex;align-items:center;justify-content:center;width:16pt !important;height:16pt !important;min-width:16pt;border:2pt solid #333 !important;background:white !important;-webkit-appearance:none !important;appearance:none !important;margin-right:0.5em !important;}' +
        'input[type="checkbox"]:checked::after{content:"☑" !important;font-size:13pt !important;color:#000 !important;}' +
        '.worksheet-question label{display:flex;align-items:flex-start;gap:0.65em;margin-bottom:0.5em;font-size:10pt;line-height:1.5;}' +
        '.text-sm,.question-points{font-size:9pt !important;color:#666 !important;font-style:italic;}' +
        '.no-print,button,.worksheet-print-button{display:none !important;}';

      const openPrint = () => {
        const w = window.open('', '_blank');
        if (!w || w.closed || typeof w.closed === 'undefined') {
          const iframe = document.createElement('iframe');
          iframe.style.position = 'fixed';
          iframe.style.right = '-9999px';
          iframe.style.bottom = '-9999px';
          document.body.appendChild(iframe);
          iframe.contentDocument.open();
          iframe.contentDocument.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title} - Print</title><style>${styles}</style></head><body>${headerHtml}${clone.outerHTML}</body></html>`);
          iframe.contentDocument.close();
          iframe.onload = () => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            setTimeout(() => iframe.remove(), 500);
          };
          return;
        }
        w.document.open();
        w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title} - Print</title><style>${styles}</style></head><body>${headerHtml}${clone.outerHTML}</body></html>`);
        w.document.close();
        w.onload = () => {
          w.focus();
          setTimeout(() => {
            w.print();
          }, 300);
        };
      };

      openPrint();
    } catch (err) {
      console.error('printWorksheet failed', err);
      alert('Unable to open print view. Please try again.');
    }
  };

  const renderQuestion = (q, idx) => {
    switch (q.type) {
      case 'multiple-choice':
      case 'mcq': {
        const name = `${containerId}-${q.id}-mcq`;
        return (
          <div className="space-y-1">
            {(q.options || []).map((opt, oi) => (
              <label key={oi} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={name}
                  data-question-id={q.id}
                  value={String(opt)}
                  checked={responses[q.id] === String(opt)}
                  onChange={(e) => handleChange(q.id, e.target.value)}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        );
      }
      case 'instructions':
        return (
          <div className="mb-2">
            {(q.prompt || q.title) && (
              <p className="font-medium">{q.prompt || q.title}</p>
            )}
            {q.content && (
              <p className="text-gray-700 whitespace-pre-wrap">{q.content}</p>
            )}
          </div>
        );
      case 'short-answer':
      default:
        return (
          <input
            type="text"
            className="w-full p-2 border border-gray-300 rounded"
            data-question-id={q.id}
            name={`${containerId}-${q.id}`}
            placeholder={q.placeholder || 'Your answer'}
            value={responses[q.id] || ''}
            onChange={(e) => handleChange(q.id, e.target.value)}
          />
        );
    }
  };

  return (
    <div id={containerId} className="my-6 p-4 rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold">{data.title || 'Worksheet'}</h3>
        {!isEditMode && (
          <div className="no-print flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                (window.printWorksheet || printWorksheetOnly)(containerId)
              }
              className="worksheet-print-button inline-flex items-center px-3 py-1.5 rounded bg-slate-700 text-white text-sm hover:bg-slate-800"
              aria-label="Print worksheet"
            >
              Print Worksheet Only
            </button>
            <button
              type="button"
              onClick={exportAnswers}
              className="inline-flex items-center px-3 py-1.5 rounded bg-slate-600 text-white text-sm hover:bg-slate-700"
            >
              Export Answers
            </button>
            <div>
              <input
                id={importInputId}
                type="file"
                accept="application/json"
                className="hidden"
                onChange={handleAnswerImport}
              />
              <button
                type="button"
                onClick={() => document.getElementById(importInputId)?.click()}
                className="inline-flex items-center px-3 py-1.5 rounded bg-slate-500 text-white text-sm hover:bg-slate-600"
              >
                Import Answers
              </button>
            </div>
          </div>
        )}
      </div>
      {data.description && (
        <p className="text-gray-700 mb-4 whitespace-pre-wrap">{data.description}</p>
      )}
      <div className="text-sm text-gray-500 mb-2">{showPoints && total ? `Total Points: ${total}` : ''}</div>

      {normalizedQuestions.length > 0 ? (
        <ol className="space-y-4 list-decimal ml-6">
          {normalizedQuestions.map((q, i) => (
            <li
              key={q.id}
              data-testid="worksheet-question"
              className="pl-2 space-y-2 list-decimal"
            >
              <div className="flex items-start gap-2 justify-between">
                <div className="flex-1">
                  <p className="font-medium">
                    <span className="mr-1">{q.label}</span>
                    {q.prompt}
                  </p>
                  {renderQuestion(q, i)}
                </div>
                {showPoints && q.points > 0 && (
                  <span className="text-sm text-gray-500 whitespace-nowrap">({q.points} pts)</span>
                )}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <div className="text-sm text-gray-500">
          No questions were provided for this worksheet.
        </div>
      )}
    </div>
  );
}
// --- React WorksheetBuilder + processor additions ---
// These are additive exports to work with ContentModal's import:
//   import { WorksheetBuilder, processWorksheetData } from './WorksheetModule';

// Normalize a single question into the export-friendly shape used by exportUtils.js
export const normalizeQuestion = (q) => {
  const base = {
    type: q.type || 'short-answer',
    title: q.title || q.prompt || q.question || '',
    points: Number.isFinite(q.points) ? q.points : (q.points ? Number(q.points) : 0),
    content: q.content || q.instructions || ''
  };
  switch (base.type) {
    case 'mcq':
      return {
        ...base,
        options: Array.isArray(q.options) ? q.options.filter(Boolean) : [],
        mode: q.mode === 'multiple' ? 'multiple' : 'single',
        allowSelectAll: q.mode === 'multiple' ? !!q.allowSelectAll : undefined,
        correctIndex:
          q.mode !== 'multiple' && typeof q.correctIndex === 'number'
            ? q.correctIndex
            : -1,
        correctIndices:
          q.mode === 'multiple' && Array.isArray(q.correctIndices)
            ? q.correctIndices
            : []
      };
    case 'true-false':
      return { ...base, correct: typeof q.correct === 'boolean' ? q.correct : null };
    case 'fill-blank':
    case 'short-answer':
    case 'long-answer':
    case 'instructions':
    default:
      return base;
  }
};

// Turn the formData coming from ContentModal into a worksheet block payload
export const processWorksheetData = (formData) => {
  const questions = Array.isArray(formData.worksheetQuestions) ? formData.worksheetQuestions : [];
  return {
    type: 'worksheet',
    title: formData.worksheetTitle || 'Worksheet',
    description: formData.worksheetDescription || '',
    totalPoints: formData.worksheetUsePoints ? Number(formData.worksheetTotalPoints) : undefined,
    usePoints: formData.worksheetUsePoints,
    questions: questions.map(normalizeQuestion)
  };
};

// Shared so the AI Prompt page and the LLM contract offer the same set — see
// src/utils/worksheetQuestionTypes.js.
const TYPE_OPTIONS = WORKSHEET_QUESTION_TYPES.map(({ value, label }) => ({ value, label }));

const defaultQuestion = (type = 'short-answer') => {
  const base = { type, title: '', points: 0, content: '' };
  if (type === 'mcq')
    return {
      ...base,
      options: [''],
      mode: 'single',
      allowSelectAll: true,
      correctIndex: -1,
      correctIndices: []
    };
  if (type === 'true-false') return { ...base, correct: null };
  return base;
};

/** Lightweight React builder for worksheets used inside the React modal. */
export const WorksheetBuilder = ({ formData, setFormData }) => {
  const [newType, setNewType] = useState('instructions');
  const [pasteModalOpen, setPasteModalOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');

  const qList = Array.isArray(formData.worksheetQuestions) ? formData.worksheetQuestions : [];

  const update = (patch) => setFormData({ ...formData, ...patch });

  const addQuestion = () => {
    const next = [...qList, defaultQuestion(newType)];
    update({ worksheetQuestions: next });
  };
  const removeQuestion = (idx) => {
    const next = qList.filter((_, i) => i !== idx);
    update({ worksheetQuestions: next });
  };
  const updateQuestion = (idx, patch) => {
    const next = qList.map((q, i) => (i === idx ? { ...q, ...patch } : q));
    update({ worksheetQuestions: next });
  };

  const changeQuestionType = (idx, newType) => {
    const prev = qList[idx] || {};
    const base = { ...defaultQuestion(newType), title: prev.title || '', points: prev.points || 0 };
    if (prev.content) base.content = prev.content;
    updateQuestion(idx, base);
  };

  const applyJsonData = (data) => {
    if (!Array.isArray(data.questions)) {
      alert('Invalid JSON: "questions" must be an array');
      return false;
    }

    const mapped = data.questions.map((q) => {
      // Detect if this question already matches the new worksheet schema
      const isNewFormat =
        q &&
        typeof q === 'object' &&
        (q.title !== undefined ||
          ['mcq', 'true-false', 'short-answer', 'long-answer', 'fill-blank', 'instructions'].includes(q.type));

      if (isNewFormat) {
        return normalizeQuestion(q);
      }

      // Fallback: support legacy schema used by older exports
      const pts = Number.isFinite(q.points) ? q.points : 0;
      switch (q.type) {
        case 'longAnswer':
          return { type: 'long-answer', title: q.prompt || '', points: pts };
        case 'multipleChoice':
          return {
            type: 'mcq',
            title: q.prompt || '',
            options: Array.isArray(q.options) ? q.options : [],
            mode: 'single',
            allowSelectAll: true,
            correctIndex: typeof q.correctIndex === 'number' ? q.correctIndex : -1,
            correctIndices: [],
            points: pts,
          };
        case 'checkboxes':
          return {
            type: 'mcq',
            title: q.prompt || '',
            options: Array.isArray(q.options) ? q.options : [],
            mode: 'multiple',
            allowSelectAll: true,
            correctIndex: -1,
            correctIndices: Array.isArray(q.correctIndices) ? q.correctIndices : [],
            points: pts,
          };
        case 'shortAnswer':
        default:
          return { type: 'short-answer', title: q.prompt || '', points: pts };
      }
    });

    setFormData(prev => ({
      ...prev,
      worksheetTitle: data.title || prev.worksheetTitle || 'Worksheet',
      worksheetDescription: data.instructions || prev.worksheetDescription || '',
      worksheetQuestions: mapped,
      worksheetUsePoints: data.usePoints === false ? false : mapped.some(q => q.points > 0),
      worksheetTotalPoints: (data.usePoints === false ? '' : mapped.reduce((sum, q) => sum + (q.points || 0), 0)),
    }));

    alert('Worksheet imported successfully.');
    return true;
  };

  // WORKSHEET JSON IMPORT
  const handleJsonImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      applyJsonData(data);
    } catch (err) {
      console.error(err);
      alert('Failed to parse JSON. Please check the schema.');
    } finally {
      e.target.value = '';
    }
  };

  const handlePasteImport = () => {
    try {
      const data = JSON.parse(pasteText);
      const ok = applyJsonData(data);
      if (ok) {
        setPasteModalOpen(false);
        setPasteText('');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to parse JSON. Please check the schema.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <input
          id="ws-use-points"
          type="checkbox"
          checked={formData.worksheetUsePoints !== false}
          onChange={(e) => update({ worksheetUsePoints: e.target.checked })}
        />
        <label htmlFor="ws-use-points" className="text-sm font-medium text-gray-700">Use Points</label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="col-span-1 md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Worksheet Title</label>
          <input
            className="w-full p-2 border border-gray-300 rounded-lg"
            value={formData.worksheetTitle || ''}
            onChange={(e) => update({ worksheetTitle: e.target.value })}
            placeholder="e.g., Timing & Spacing Checkpoint"
          />
        </div>
        {formData.worksheetUsePoints !== false && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Points (optional)</label>
            <input
              type="number"
              min="0"
              className="w-full p-2 border border-gray-300 rounded-lg"
              value={formData.worksheetTotalPoints || ''}
              onChange={(e) => update({ worksheetTotalPoints: e.target.value })}
              placeholder="e.g., 20"
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description / Instructions</label>
        <textarea
          rows={3}
          className="w-full p-2 border border-gray-300 rounded-lg"
          value={formData.worksheetDescription || ''}
          onChange={(e) => update({ worksheetDescription: e.target.value })}
          placeholder="Brief directions for students…"
        />
      </div>

      {/* WORKSHEET JSON IMPORT */}
      <div className="no-print">
        <label className="block text-sm font-medium mb-1">Import Worksheet JSON</label>
        <div className="flex items-center gap-2">
          <input type="file" accept="application/json" onChange={handleJsonImport} />
          <button
            type="button"
            className="px-2 py-1 text-sm bg-gray-200 rounded"
            onClick={() => setPasteModalOpen(true)}
          >
            Paste JSON
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">Choose a .json file or paste JSON to load a worksheet.</p>
      </div>

      {pasteModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-4">Paste Worksheet JSON</h3>
            <p className="text-sm text-gray-600 mb-4">
              Paste the contents of a worksheet .json file below and click Import. This will replace any existing questions.
            </p>
            <textarea
              className="w-full h-64 p-2 border border-gray-300 rounded"
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder='{"title":"Worksheet","questions":[]}'
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 rounded"
                onClick={() => { setPasteModalOpen(false); setPasteText(''); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-blue-600 text-white rounded"
                onClick={handlePasteImport}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Questions */}
      <div className="border-t pt-4">
        <div className="flex items-center gap-2 mb-3">
          <select
            className="p-2 border border-gray-300 rounded-lg"
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="px-3 py-2 bg-blue-600 text-white rounded-lg"
            onClick={addQuestion}
          >
            Add Question
          </button>
        </div>

        {qList.length === 0 && (
          <p className="text-sm text-gray-500">No questions yet. Choose a type and click “Add Question”.</p>
        )}

        <div className="space-y-4">
          {qList.map((q, idx) => (
            <div key={idx} className="p-3 border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Q{idx + 1}</span>
                <div className="flex items-center gap-2">
                  <select
                    className="p-1 border border-gray-300 rounded text-sm"
                    value={q.type}
                    onChange={(e) => changeQuestionType(idx, e.target.value)}
                  >
                    {TYPE_OPTIONS.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="text-red-600 text-sm"
                    onClick={() => removeQuestion(idx)}
                  >
                    Remove
                  </button>
                </div>
              </div>

              {/* Common fields */}
              {q.type !== 'instructions' && (
                <>
                  <label className="block text-xs text-gray-600 mb-1">Question</label>
                  <input
                    className="w-full p-2 border border-gray-300 rounded-lg mb-2"
                    value={q.title || ''}
                    onChange={(e) => updateQuestion(idx, { title: e.target.value })}
                    placeholder="Enter question text…"
                  />
                  {formData.worksheetUsePoints !== false && (
                    <>
                      <label className="block text-xs text-gray-600 mb-1">Points</label>
                      <input
                        type="number"
                        min="0"
                        className="w-32 p-2 border border-gray-300 rounded-lg mb-2"
                        value={q.points ?? 0}
                        onChange={(e) => updateQuestion(idx, { points: Number(e.target.value || 0) })}
                      />
                    </>
                  )}
                </>
              )}

              {/* Type-specific */}
              {q.type === 'instructions' && (
                <>
                  <label className="block text-xs text-gray-600 mb-1">Title</label>
                  <input
                    className="w-full p-2 border border-gray-300 rounded-lg mb-2"
                    value={q.title || ''}
                    onChange={(e) => updateQuestion(idx, { title: e.target.value })}
                    placeholder="Section or instructions heading…"
                  />
                  <label className="block text-xs text-gray-600 mb-1">Content</label>
                  <textarea
                    rows={3}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={q.content || ''}
                    onChange={(e) => updateQuestion(idx, { content: e.target.value })}
                    placeholder="Guidance for students…"
                  />
                </>
              )}

              {q.type === 'mcq' && (
                <>
                  <label className="block text-xs text-gray-600 mb-1">Options</label>
                  {(Array.isArray(q.options) ? q.options : ['']).map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2 mb-2">
                      <input
                        className="flex-1 p-2 border border-gray-300 rounded-lg"
                        value={opt}
                        onChange={(e) => {
                          const opts = [...(q.options || [])];
                          opts[oi] = e.target.value;
                          updateQuestion(idx, { options: opts });
                        }}
                        placeholder={`Option ${oi + 1}`}
                      />
                      <button
                        type="button"
                        className="px-2 py-1 text-sm border rounded"
                        onClick={() => {
                          const opts = [...(q.options || [])];
                          opts.splice(oi, 1);
                          updateQuestion(idx, { options: opts });
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="px-2 py-1 text-sm border rounded mb-2"
                    onClick={() => updateQuestion(idx, { options: [...(q.options || []), ''] })}
                  >
                    Add Option
                  </button>

                  <label className="block text-xs text-gray-600 mb-1">Answer Selection</label>
                  <select
                    className="p-2 border border-gray-300 rounded-lg mb-2"
                    value={q.mode || 'single'}
                    onChange={(e) => {
                      const mode = e.target.value;
                      if (mode === 'multiple') {
                        updateQuestion(idx, {
                          mode,
                          allowSelectAll: true,
                          correctIndices:
                            typeof q.correctIndex === 'number' && q.correctIndex > -1
                              ? [q.correctIndex]
                              : q.correctIndices || [],
                          correctIndex: -1,
                        });
                      } else {
                        updateQuestion(idx, {
                          mode,
                          correctIndex:
                            Array.isArray(q.correctIndices) && q.correctIndices.length > 0
                              ? q.correctIndices[0]
                              : q.correctIndex ?? -1,
                        });
                      }
                    }}
                  >
                    <option value="single">Single choice</option>
                    <option value="multiple">Multiple choice</option>
                  </select>

                  {q.mode === 'multiple' && (
                    <label className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                      <input
                        type="checkbox"
                        checked={q.allowSelectAll ?? true}
                        onChange={(e) =>
                          updateQuestion(idx, { allowSelectAll: e.target.checked })
                        }
                      />
                      Allow selecting all options
                    </label>
                  )}

                  {q.mode === 'single' && (
                    <>
                      <label className="block text-xs text-gray-600 mb-1">Correct Option</label>
                      <select
                        className="p-2 border border-gray-300 rounded-lg"
                        value={typeof q.correctIndex === 'number' ? q.correctIndex : -1}
                        onChange={(e) =>
                          updateQuestion(idx, { correctIndex: Number(e.target.value) })
                        }
                      >
                        <option value={-1}>— none —</option>
                        {(q.options || []).map((_, oi) => (
                          <option key={oi} value={oi}>{`Option ${oi + 1}`}</option>
                        ))}
                      </select>
                    </>
                  )}

                  {q.mode === 'multiple' && (
                    <>
                      <label className="block text-xs text-gray-600 mb-1">Correct Options</label>
                      {(q.options || []).map((_, oi) => (
                        <label key={oi} className="flex items-center gap-1 text-sm mb-1">
                          <input
                            type="checkbox"
                            checked={Array.isArray(q.correctIndices) ? q.correctIndices.includes(oi) : false}
                            onChange={(e) => {
                              const arr = Array.isArray(q.correctIndices) ? [...q.correctIndices] : [];
                              if (e.target.checked) {
                                if (!arr.includes(oi)) arr.push(oi);
                              } else {
                                const pos = arr.indexOf(oi);
                                if (pos > -1) arr.splice(pos, 1);
                              }
                              updateQuestion(idx, { correctIndices: arr });
                            }}
                          />
                          {`Option ${oi + 1}`}
                        </label>
                      ))}
                    </>
                  )}
                </>
              )}

              {q.type === 'true-false' && (
                <>
                  <label className="block text-xs text-gray-600 mb-1">Correct Answer</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1 text-sm">
                      <input
                        type="radio"
                        checked={q.correct === true}
                        onChange={() => updateQuestion(idx, { correct: true })}
                      />
                      True
                    </label>
                    <label className="flex items-center gap-1 text-sm">
                      <input
                        type="radio"
                        checked={q.correct === false}
                        onChange={() => updateQuestion(idx, { correct: false })}
                      />
                      False
                    </label>
                  </div>
                  <label className="block text-xs text-gray-600 mt-2 mb-1">Optional prompt</label>
                  <input
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={q.title || ''}
                    onChange={(e) => updateQuestion(idx, { title: e.target.value })}
                    placeholder="e.g., Is the following statement true or false…"
                  />
                </>
              )}

              {(q.type === 'short-answer' || q.type === 'long-answer' || q.type === 'fill-blank') && (
                <>
                  <label className="block text-xs text-gray-600 mb-1">
                    {q.type === 'fill-blank'
                      ? 'Sentence with blanks (use "_____")'
                      : 'Additional prompt / instructions (optional)'}
                  </label>
                  <textarea
                    rows={q.type === 'long-answer' ? 4 : 2}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={q.content || ''}
                    onChange={(e) => updateQuestion(idx, { content: e.target.value })}
                    placeholder={
                      q.type === 'fill-blank'
                        ? 'e.g., The principle of ____ controls how far apart drawings are spaced.'
                        : 'e.g., Answer in 2–3 sentences'
                    }
                  />
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
